import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { playersAPI, teamsAPI, seasonsAPI } from '../api/api';
import { useTranslation } from 'react-i18next';

const PlayerManagement = () => {
  const { isAdmin } = useAuth();
  const { t } = useTranslation();
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [formData, setFormData] = useState({});
  const [activeSeason, setActiveSeason] = useState(null);

  const canEdit = isAdmin();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Get active season first to determine filtering logic
      let currentActiveSeason = null;
      let teamsToLoad = null;
      
      try {
        const activeSeasonRes = await seasonsAPI.getActive();
        currentActiveSeason = activeSeasonRes.data;
        setActiveSeason(currentActiveSeason);
      } catch (error) {
        setActiveSeason(null);
      }
      
      // Load teams based on season type:
      // - Custom seasons: Only teams with GMs and players (activeOnly=true)
      // - Regular seasons or no season: All teams
      if (currentActiveSeason && currentActiveSeason.seasonType === 'custom') {
        teamsToLoad = teamsAPI.getAllFiltered(currentActiveSeason.id, true);
      } else {
        teamsToLoad = teamsAPI.getAll();
      }
      
      const [playersRes, teamsRes, freeAgentsRes] = await Promise.all([
        playersAPI.getAll(true),
        teamsToLoad,
        playersAPI.getFreeAgents()
      ]);

      // Merge rostered players with those not yet in the active season roster.
      // Dedup by id — rostered entry wins if somehow a player appears in both.
      const rosteredIds = new Set(playersRes.data.map(p => p.id));
      const unrostered = (freeAgentsRes.data || []).map(p => ({ ...p, notInSeasonRoster: true }));
      const merged = [
        ...playersRes.data,
        ...unrostered.filter(p => !rosteredIds.has(p.id))
      ];

      setPlayers(merged);
      setTeams(teamsRes.data);
      
      
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Failed to load players: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (player) => {
    setEditingPlayer(player.id);
    setFormData({
      firstName: player.firstName,
      lastName: player.lastName,
      position: player.position,
      overallRating: player.overallRating,
      jerseyNumber: player.jerseyNumber || '',
      height: player.height || '',
      weight: player.weight || '',
      badges: player.badges || '',
      teamId: player.teamId
    });
  };

  const handleCancelEdit = () => {
    setEditingPlayer(null);
    setFormData({});
  };

  const handleSavePlayer = async () => {
    try {
      // Validate required fields
      if (!formData.firstName || !formData.lastName || !formData.position || !formData.overallRating) {
        alert(t('admin.fillRequiredFields'));
        return;
      }

      // Validate position
      const validPositions = ['PG', 'SG', 'SF', 'PF', 'C', 'PG/SG', 'SG/PG', 'SG/SF', 'SF/SG', 'SF/PF', 'PF/SF', 'C/PF', 'PF/C'];
      if (!validPositions.includes(formData.position)) {
        alert(t('admin.invalidPosition') + ': ' + validPositions.join(', '));
        return;
      }

      // Validate overall rating
      const rating = parseInt(formData.overallRating);
      if (isNaN(rating) || rating < 0 || rating > 99) {
        alert('Overall rating must be between 0 and 99');
        return;
      }

      // Prepare data for API
      const updateData = {
        ...formData,
        overallRating: rating,
        teamId: formData.teamId != null ? parseInt(formData.teamId) : null,
        jerseyNumber: formData.jerseyNumber ? parseInt(formData.jerseyNumber) : null,
        weight: formData.weight ? parseInt(formData.weight) : null,
        badges: formData.badges ? parseInt(formData.badges) : null
      };

      await playersAPI.update(editingPlayer, updateData);
      
      // Reload data
      await loadData();
      setEditingPlayer(null);
      setFormData({});
      alert('Player updated successfully!');
    } catch (error) {
      console.error('Error saving player:', error);
      alert('Failed to save player: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Filter players
  const filteredPlayers = players.filter(player => {
    const matchesTeam =
      selectedTeam === 'all' ||
      (selectedTeam === 'not_in_season' ? player.notInSeasonRoster === true :
      selectedTeam === 'free_agents' ? !player.teamId :
      player.teamId === parseInt(selectedTeam));
    const matchesSearch = searchTerm === '' || 
      `${player.firstName} ${player.lastName}`.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesTeam && matchesSearch;
  });

  // Sort by overall rating (desc), then by last name
  const sortedPlayers = [...filteredPlayers].sort((a, b) => {
    const ovrDiff = (b.overallRating || 0) - (a.overallRating || 0);
    if (ovrDiff !== 0) return ovrDiff;
    return (a.lastName || '').localeCompare(b.lastName || '');
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-xl text-gray-600 dark:text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-2">{t('admin.playerManagementTitle')}</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-3">
          {canEdit 
            ? 'View and edit all players in the league' 
            : 'View all players in the league (read-only access)'}
        </p>
        
        {/* Season Filter Info */}
        {activeSeason && (
          <div className={`inline-flex items-center px-3 py-2 rounded-lg text-sm ${
            activeSeason.seasonType === 'custom' 
              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800' 
              : 'bg-green-100 dark:bg-green-900/30 text-green-800'
          }`}>
            <span className="mr-2">🏀</span>
            <strong>{activeSeason.name}</strong>
            <span className="mx-2">•</span>
            {activeSeason.seasonType === 'custom' 
              ? 'Showing only teams with GMs and active players' 
              : 'Showing all teams'}
          </div>
        )}
        
        {!activeSeason && (
          <div className="inline-flex items-center px-3 py-2 rounded-lg text-sm bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
            <span className="mr-2">ℹ️</span>
            No active season • Showing all teams
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Team Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('admin.teamManagement.teamName')}
            </label>
            <select
              value={selectedTeam}
              onChange={(e) => setSelectedTeam(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
            >
              <option value="all">{t('admin.allTeams')}</option>
              <option value="free_agents">Free Agents (No Team)</option>
              {activeSeason && <option value="not_in_season">⚠️ Not in Season Roster</option>}
              {teams.map(team => (
                <option key={team.id} value={team.id}>
                  {team.city} {team.name}
                </option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('admin.searchPlayers')}
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('admin.searchPlayers')}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
            />
          </div>
        </div>

        <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
          Showing {sortedPlayers.length} of {players.length} players
        </div>
      </div>

      {/* Players List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-100 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Team</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300">Position</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300">OVR</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300">Jersey</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300">Height</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300">Weight</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300">Badges</th>
                {canEdit && <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {sortedPlayers.map((player) => (
                <tr key={player.id} className="border-b hover:bg-gray-50 dark:bg-gray-800">
                  {editingPlayer === player.id ? (
                    // Edit Mode
                    <>
                      <td className="px-4 py-3">
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={formData.firstName}
                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                            placeholder="First Name"
                            className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm"
                          />
                          <input
                            type="text"
                            value={formData.lastName}
                            onChange={(e) => handleInputChange('lastName', e.target.value)}
                            placeholder="Last Name"
                            className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={formData.teamId ?? ''}
                          onChange={(e) => handleInputChange('teamId', e.target.value === '' ? null : parseInt(e.target.value))}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm"
                        >
                          <option value="">Free Agent (No Team)</option>
                          {teams.map(team => (
                            <option key={team.id} value={team.id}>
                              {team.city} {team.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={formData.position}
                          onChange={(e) => handleInputChange('position', e.target.value)}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm"
                        >
                          <option value="PG">PG</option>
                          <option value="SG">SG</option>
                          <option value="SF">SF</option>
                          <option value="PF">PF</option>
                          <option value="C">C</option>
                          <option value="PG/SG">PG/SG</option>
                          <option value="SG/PG">SG/PG</option>
                          <option value="SG/SF">SG/SF</option>
                          <option value="SF/SG">SF/SG</option>
                          <option value="SF/PF">SF/PF</option>
                          <option value="PF/SF">PF/SF</option>
                          <option value="C/PF">C/PF</option>
                          <option value="PF/C">PF/C</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          value={formData.overallRating}
                          onChange={(e) => handleInputChange('overallRating', e.target.value)}
                          min="0"
                          max="99"
                          className="w-20 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm text-center"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          value={formData.jerseyNumber}
                          onChange={(e) => handleInputChange('jerseyNumber', e.target.value)}
                          placeholder="-"
                          className="w-16 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm text-center"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={formData.height}
                          onChange={(e) => handleInputChange('height', e.target.value)}
                          placeholder="6-6"
                          className="w-20 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm text-center"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          value={formData.weight}
                          onChange={(e) => handleInputChange('weight', e.target.value)}
                          placeholder="lbs"
                          className="w-20 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm text-center"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          value={formData.badges}
                          onChange={(e) => handleInputChange('badges', e.target.value)}
                          placeholder="0"
                          className="w-16 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm text-center"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2 justify-center">
                          <button
                            onClick={handleSavePlayer}
                            className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                          >
                            ✓ Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="bg-gray-50 dark:bg-gray-8000 text-white px-3 py-1 rounded text-sm hover:bg-gray-600"
                          >
                            ✕ Cancel
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    // View Mode
                    <>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {player.firstName} {player.lastName}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                          {player.team
                            ? `${player.team.city} ${player.team.name}`
                            : <span className="italic text-gray-400">Free Agent</span>}
                        </div>
                        {player.isBenched && (
                          <span className="inline-block mt-1 mr-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-1.5 py-0.5 rounded font-medium">
                            {t('bench.benchedAdminBadge')}
                          </span>
                        )}
                        {player.notInSeasonRoster && (
                          <span className="inline-block mt-1 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 px-1.5 py-0.5 rounded font-medium">
                            ⚠️ Not in roster
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 px-2 py-1 rounded text-xs font-semibold">
                          {player.position}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`font-bold ${
                          player.overallRating >= 85 ? 'text-green-600 dark:text-green-400' :
                          player.overallRating >= 80 ? 'text-blue-600 dark:text-blue-400' :
                          player.overallRating >= 75 ? 'text-yellow-600' :
                          'text-gray-600 dark:text-gray-400'
                        }`}>
                          {player.overallRating}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-gray-600 dark:text-gray-400">
                        {player.jerseyNumber || '-'}
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-gray-600 dark:text-gray-400">
                        {player.height || '-'}
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-gray-600 dark:text-gray-400">
                        {player.weight ? `${player.weight} lbs` : '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {player.badges ? (
                          <span className="text-yellow-600 font-semibold">
                            {player.badges}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      {canEdit && (
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleEditClick(player)}
                            className="bg-blue-600 text-white px-4 py-1 rounded text-sm hover:bg-blue-700"
                          >
                            ✏️ Edit
                          </button>
                        </td>
                      )}
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {sortedPlayers.length === 0 && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            No players found matching your filters
          </div>
        )}
      </div>

      {/* Info Box */}
      {!canEdit && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <span className="text-2xl">ℹ️</span>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-800">
                <strong>Read-Only Access:</strong> As a Team Owner, you can view all players but cannot edit them. Contact an administrator for player updates.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerManagement;
